import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { Icons } from "../../doctor-console/Utils/iconsRegistry";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import axios from "axios";
import { toast } from "react-toastify";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CancelAllModal from "./CancelAllModal";
import DoctorLeaves from "./DoctorLeaves";
dayjs.extend(utc);
dayjs.extend(timezone);

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const CustomDoctorScheduleModal = ({ open, handleClose, doctorId }) => {
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const [invalidShifts, setInvalidShifts] = useState([]);
  const [noSchedule, setNoSchedule] = useState(false);
  const [openCancelAllModal, setOpenCancelAllModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState("custom schedule");

  // Week navigation and dates (IST)
  const [currentWeekStart, setCurrentWeekStart] = useState(
    // ensure we pick IST zoned date and startOf('week') (Sunday based)
    () => dayjs().tz("Asia/Kolkata").startOf("week")
  );

  const isPastWeek = () => {
    var pastWeek = currentWeekStart.isBefore(
      dayjs().tz("Asia/Kolkata").startOf("week"),
      "day"
    );
    return pastWeek;
  };

  console.log("currentWeekStart", currentWeekStart);

  const getWeekDates = (startOfWeek) => {
    return Array.from({ length: 7 }, (_, i) =>
      dayjs(startOfWeek).add(i, "day").format("YYYY-MM-DD")
    );
  };

  const weekDates = getWeekDates(currentWeekStart);

  // Initialize schedule with date included
  const initialSchedule = daysOfWeek.map((day, index) => ({
    day,
    date: weekDates[index],
    hasOccupied: false,
    shifts: [
      { start: null, end: null, patientCount: 0 },
      { start: null, end: null, patientCount: 0 },
      { start: null, end: null, patientCount: 0 },
    ],
  }));

  const [schedule, setSchedule] = useState(initialSchedule);

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  console.log("schedule", schedule);

  useEffect(() => {
    if (open && doctorId) {
      fetchDoctorSchedule(doctorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doctorId, currentWeekStart]);

  // whenever week changes, update dates in schedule
  useEffect(() => {
    setSchedule((prev) =>
      prev.map((item, idx) => ({
        ...item,
        date: getWeekDates(currentWeekStart)[idx],
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => prev.subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => prev.add(1, "week"));
  };

  const closeHandler = () => {
    setSchedule(
      daysOfWeek.map((day, index) => ({
        day,
        date: getWeekDates(currentWeekStart)[index],
        shifts: [
          { start: null, end: null, patientCount: 0 },
          { start: null, end: null, patientCount: 0 },
          { start: null, end: null, patientCount: 0 },
        ],
      }))
    );
    setNoSchedule(false);
    handleClose();
  };

  const fetchDoctorSchedule = async (doctorId) => {
    try {
      const res = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_custom_schedules/${doctorId}/?date_from=${
          getWeekDates(currentWeekStart)[0]
        }&date_to=${getWeekDates(currentWeekStart)[6]}`,
        { headers }
      );

      if (!res?.data || res.data.status !== "Success") {
        if (
          res?.data?.message?.includes("No Schedules found for this Doctor")
        ) {
          setNoSchedule(true);
        } else {
          console.warn(
            "Doctor schedule API did not return Success:",
            res?.data
          );
        }
        return;
      }

      if (res.data?.data?.length) {
        const apiData = res.data.data;
        const weekDates = getWeekDates(currentWeekStart);

        const updatedSchedule = daysOfWeek.map((day, idx) => {
          const apiDay = apiData.find(
            (d) => d.shift_day.toLowerCase() === day.toLowerCase()
          );

          if (apiDay) {
            return {
              day,
              date: weekDates[idx], // Keep the date for the week
              hasOccupied: apiDay.has_occupied,
              shifts: [
                {
                  start: apiDay.first_shift
                    ? dayjs(apiDay.first_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.first_shift
                    ? dayjs(apiDay.first_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.first_shift_patient_visit_count || 0,
                },
                {
                  start: apiDay.second_shift
                    ? dayjs(apiDay.second_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.second_shift
                    ? dayjs(apiDay.second_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.second_shift_patient_visit_count || 0,
                },
                {
                  start: apiDay.third_shift
                    ? dayjs(apiDay.third_shift_start_time, "HH:mm:ss")
                    : null,
                  end: apiDay.third_shift
                    ? dayjs(apiDay.third_shift_end_time, "HH:mm:ss")
                    : null,
                  patientCount: apiDay.third_shift_patient_visit_count || 0,
                },
              ],
            };
          } else {
            // Default empty shifts for missing API days
            return {
              day,
              date: weekDates[idx], // still assign the date
              shifts: [
                { start: null, end: null, patientCount: 0 },
                { start: null, end: null, patientCount: 0 },
                { start: null, end: null, patientCount: 0 },
              ],
            };
          }
        });

        setSchedule(updatedSchedule);
      }
    } catch (error) {
      console.error("Error fetching doctor schedule:", error);
    }
  };

  const handleTimeChange = (dayIndex, shiftIndex, field, value) => {
    const updated = [...schedule];
    updated[dayIndex].shifts[shiftIndex][field] = value;
    setSchedule(updated);
  };

  function adjustTimeToLocal(inputTime) {
    // Convert the input UTC time string to a Date object
    const date = new Date(inputTime);

    // Convert the UTC time to the local time zone
    const localTime = new Date(
      date.toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    // Format the time as HH:mm:ss (local time)
    const hours = String(localTime.getHours()).padStart(2, "0");
    const minutes = String(localTime.getMinutes()).padStart(2, "0");
    const seconds = String(localTime.getSeconds()).padStart(2, "0");

    // Return the formatted time
    return `${hours}:${minutes}:${seconds}`;
  }

  const prepareDoctorSchedulePayload = (doctorId, schedule) => {
    return schedule
      .filter((dayItem) => !dayItem.hasOccupied) // include only days whose hasOccupied is false
      .map((dayItem) => ({
        execution_date: dayItem.date,
        shift_day: dayItem.day.toLowerCase(),
        first_shift: !!(dayItem.shifts[0].start && dayItem.shifts[0].end),
        first_shift_start_time: !!(
          dayItem.shifts[0].start && dayItem.shifts[0].end
        )
          ? adjustTimeToLocal(dayItem.shifts[0].start)
          : null,
        first_shift_end_time: !!(
          dayItem.shifts[0].start && dayItem.shifts[0].end
        )
          ? adjustTimeToLocal(dayItem.shifts[0].end)
          : null,
        first_shift_patient_visit_count: dayItem.shifts[0].patientCount || 0,

        second_shift: !!(dayItem.shifts[1].start && dayItem.shifts[1].end),
        second_shift_start_time: !!(
          dayItem.shifts[1].start && dayItem.shifts[1].end
        )
          ? adjustTimeToLocal(dayItem.shifts[1].start)
          : null,
        second_shift_end_time: !!(
          dayItem.shifts[1].start && dayItem.shifts[1].end
        )
          ? adjustTimeToLocal(dayItem.shifts[1].end)
          : null,
        second_shift_patient_visit_count: dayItem.shifts[1].patientCount || 0,

        third_shift: !!(dayItem.shifts[2].start && dayItem.shifts[2].end),
        third_shift_start_time: !!(
          dayItem.shifts[2].start && dayItem.shifts[2].end
        )
          ? adjustTimeToLocal(dayItem.shifts[2].start)
          : null,
        third_shift_end_time: !!(
          dayItem.shifts[2].start && dayItem.shifts[2].end
        )
          ? adjustTimeToLocal(dayItem.shifts[2].end)
          : null,
        third_shift_patient_visit_count: dayItem.shifts[2].patientCount || 0,
      }));
  };

  const validateSchedule = (schedule) => {
    const invalidShifts = [];

    for (let dayIndex = 0; dayIndex < schedule.length; dayIndex++) {
      const dayData = schedule[dayIndex];
      const shifts = dayData.shifts;

      // --- Single-shift validations ---
      for (let shiftIndex = 0; shiftIndex < shifts.length; shiftIndex++) {
        const shift = shifts[shiftIndex];

        // Rule 1: If patientCount > 0 => start & end required
        if (shift.patientCount > 0) {
          if (!shift.start || !shift.end) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time and End time must be filled`,
            });
          }

          // Rule 2: End time cannot be before start
          if (
            shift.start &&
            shift.end &&
            dayjs(shift.end).isBefore(shift.start)
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time cannot be after End time`,
            });
          }

          // Rule 3: Start and End time cannot be same
          if (
            shift.start &&
            shift.end &&
            dayjs(shift.start).isSame(shift.end)
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex,
              errorMessage: `Shift ${
                shiftIndex + 1
              }: Start time and End time cannot be the same`,
            });
          }
        }

        // Rule 4: If start/end present => patientCount must be > 0
        if ((shift.start || shift.end) && shift.patientCount <= 0) {
          invalidShifts.push({
            day: dayData.day,
            shiftIndex,
            errorMessage: `Shift ${
              shiftIndex + 1
            }: Patient count must be greater than 0 if a shift time is provided`,
          });
        }
      }

      // --- Multi-shift validations (compare all pairs) ---
      for (let i = 0; i < shifts.length; i++) {
        const shiftA = shifts[i];
        if (!shiftA.start || !shiftA.end) continue;

        const startA = dayjs(shiftA.start);
        const endA = dayjs(shiftA.end);

        for (let j = i + 1; j < shifts.length; j++) {
          const shiftB = shifts[j];
          if (!shiftB.start || !shiftB.end) continue;

          const startB = dayjs(shiftB.start);
          const endB = dayjs(shiftB.end);

          // Rule 5a: Identical start or end times between any two shifts
          if (
            startA.isSame(startB) ||
            endA.isSame(endB) ||
            (startA.isSame(startB) && endA.isSame(endB))
          ) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${i + 1} and Shift ${
                j + 1
              }: cannot have identical start or end times`,
            });
          }

          // Rule 5b: Overlapping or unordered shifts (regardless of order)
          // If shiftB starts before shiftA ends → overlap
          if (startB.isBefore(endA) && endB.isAfter(startA)) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${i + 1} overlaps with Shift ${j + 1}`,
            });
          }

          // Rule 5c: If one shift fully before another but out of order
          if (endB.isBefore(startA)) {
            invalidShifts.push({
              day: dayData.day,
              shiftIndex: j,
              errorMessage: `Shift ${j + 1} occurs before Shift ${
                i + 1
              } — shifts must be in chronological order`,
            });
          }
        }
      }
    }

    return { isValid: invalidShifts.length === 0, invalidShifts };
  };

  const onSave = async () => {
    // Check if all shifts have null or empty values (start, end, patientCount)
    const isScheduleEmpty = schedule.every((dayData) =>
      dayData.shifts.every(
        (shift) =>
          !shift.start &&
          !shift.end &&
          (shift.patientCount === 0 || shift.patientCount === "")
      )
    );

    if (isScheduleEmpty) {
      toast.error("You need to fill the schedule before saving.");
      return; // Prevent saving if schedule is empty
    }
    const { isValid, invalidShifts } = validateSchedule(schedule);

    if (!isValid) {
      // Update the state with invalid shifts for UI rendering
      setInvalidShifts(invalidShifts);

      // Display the error toast with the first invalid shift's details
      toast.error(
        `${invalidShifts[0].errorMessage} 
        on ${invalidShifts[0].day}`
        //  - Shift ${
        //   invalidShifts[0].shiftIndex + 1
        // }`
      );
      return; // Prevent saving if validation fails
    }

    const payload = prepareDoctorSchedulePayload(doctorId, schedule);

    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_custom_schedules/${doctorId}/`,
        payload,
        {
          headers: headers,
        }
      );
      console.log("res.data", res.data);
      if (!res?.data || res.data.status !== "Success") {
        toast.error("Schedule Updation Unsuccessful!");
      } else {
        toast.success("Schedule Updated Successfully!");
      }
    } catch (error) {
      console.error("Error updating doctor schedule:", error);
    }
  };

  const handleResetShift = (dayIndex, shiftIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        shifts: updated[dayIndex].shifts.map((shift, i) =>
          i === shiftIndex
            ? { start: null, end: null, patientCount: 0 } // reset only this shift
            : shift
        ),
      };
      return updated;
    });
  };

  console.log("isPastWeek", isPastWeek());

  return !noSchedule ? (
    <>
      <CancelAllModal
        open={openCancelAllModal}
        onClose={() => {
          setOpenCancelAllModal(false);
        }}
        doctorId={doctorId}
        functionsOnSuccess={() => {
          fetchDoctorSchedule(doctorId);
        }}
      />
      <Dialog
        open={open}
        onClose={closeHandler}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: "90%", // responsive width (mobile-friendly)
            maxWidth: "1500px", // never exceed 1500px
            maxHeight: "90vh",
            zIndex: 9997,
          },
        }}
      >
        <DialogTitle
          sx={{
            // backgroundImage: `url(${Icons.modal_blue})`,
            backgroundColor: "#213360",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            className="main-title"
            style={{
              textAlign: "center",
              color: "#FFFFFF",
              margin: "0px",
              fontSize: "20px",
            }}
          >
            {selectedTab === "custom schedule"
              ? "Set Custom Schedule"
              : "Doctor's Leaves"}
          </div>
          <IconButton onClick={closeHandler}>
            <CloseIcon
              sx={{
                color: "#FFFFFF",
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
              }}
            />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            maxHeight: "calc(80vh - 100px)",
            overflowY: "auto",
            overflowX: "auto",
            padding: "0px 24px",
          }}
        >
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mb={1.5}
          >
            <Tabs
              value={selectedTab}
              onChange={(e, val) => setSelectedTab(val)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#0e204d", // Set the indicator color to dark blue
                },
              }}
            >
              <Tab
                key="custom schedule"
                label="Custom Schedule"
                value="custom schedule"
                sx={{
                  textTransform: "none", // Prevent uppercase transformation
                  "&.Mui-selected": {
                    color: "#0e204d", // Set the color of the selected tab to dark blue
                  },
                }}
              />
              <Tab
                key="doctor leaves"
                label="Doctor's Leaves"
                value="doctor leaves"
                sx={{
                  textTransform: "none", // Prevent uppercase transformation
                  "&.Mui-selected": {
                    color: "#0e204d", // Set the color of the selected tab to dark blue
                  },
                }}
              />
            </Tabs>
          </Box>

          {selectedTab === "custom schedule" && (
            <>
              {/* Note Section */}
              <Box
                sx={{
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #FFECB3",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                  color: "#795548",
                  fontSize: "0.875rem",
                  lineHeight: 1.4,
                }}
              >
                <span style={{ fontSize: "18px", flexShrink: 0 }}>💡</span>

                {/* Make the text behave inline */}
                <Typography
                  component="span"
                  sx={{
                    display: "inline",
                    flex: 1,
                    wordBreak: "break-word",
                  }}
                >
                  If you see any <strong>disabled rows</strong>, it means
                  appointments are already scheduled for that date. To edit
                  schedule for that date,&nbsp;
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      whiteSpace: "nowrap", // ensures "Cancel All first." stays together
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#ffffff",
                        border: "1px solid rgb(204, 204, 204)",
                        boxShadow: "none",
                        borderRadius: "9999px",
                        textTransform: "none",
                        color: "#1a2942",
                        padding: "4px 10px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.8rem",
                        "&:hover": {
                          backgroundColor: "#f5f7fa",
                          boxShadow: "none",
                        },
                      }}
                      onClick={() => setOpenCancelAllModal(true)}
                      size="small"
                    >
                      Cancel All
                    </Button>
                    <span>&nbsp;first.</span>
                  </Box>
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={2}
                mb={1}
              >
                <IconButton onClick={handlePrevWeek}>
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#0E3F66",
                    }}
                  >
                    ←
                  </span>
                </IconButton>

                <Box
                  sx={{
                    fontWeight: 600,
                    color: "#0E3F66",
                    fontSize: "0.95rem",
                  }}
                >
                  {currentWeekStart.format("MMM DD, YYYY")} -{" "}
                  {currentWeekStart.add(6, "day").format("MMM DD, YYYY")}
                </Box>

                <IconButton onClick={handleNextWeek}>
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#0E3F66",
                    }}
                  >
                    →
                  </span>
                </IconButton>
              </Box>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Box
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  {/* Header Row */}
                  <Box
                    display="grid"
                    gridTemplateColumns="
        130px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
      "
                    alignItems="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: "#444",
                      mb: 0.5,
                      columnGap: "10px",
                      borderBottom: "2px solid #ccc",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      sx={{
                        borderRight: "2px solid #ccc",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      Day
                    </Box>
                    <Box textAlign="center" gridColumn="span 3">
                      1st Shift
                    </Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    <Box
                      sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    {/* divider */}
                    <Box textAlign="center" gridColumn="span 3">
                      2nd Shift
                    </Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    <Box
                      sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    {/* divider */}
                    <Box textAlign="center" gridColumn="span 3">
                      3rd Shift
                    </Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                  </Box>

                  {/* Sub Header Row */}
                  <Box
                    display="grid"
                    gridTemplateColumns="
        130px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
      "
                    alignItems="center"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      color: "#666",
                      columnGap: "10px",
                      borderBottom: "1px solid #ccc",
                      width: "fit-content",
                      mb: 0.5,
                    }}
                  >
                    <Box />
                    <Box textAlign="center">Start Time</Box>
                    <Box textAlign="center">End Time</Box>
                    <Box textAlign="center">Patient Count</Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    <Box
                      sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    {/* divider */}
                    <Box textAlign="center">Start Time</Box>
                    <Box textAlign="center">End Time</Box>
                    <Box textAlign="center">Patient Count</Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    <Box
                      sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                    {/* divider */}
                    <Box textAlign="center">Start Time</Box>
                    <Box textAlign="center">End Time</Box>
                    <Box textAlign="center">Patient Count</Box>
                    <Box
                    // sx={{ borderRight: "2px solid #ccc", height: "100%" }}
                    />{" "}
                  </Box>

                  {/* Schedule Rows */}
                  {schedule.map((dayData, dayIndex) => {
                    const isDisabled = dayData.hasOccupied; // check the flag once per row

                    return (
                      <Box
                        key={dayData.day}
                        display="grid"
                        gridTemplateColumns="
        130px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
        repeat(3, minmax(90px, 120px)) 15px 15px
      "
                        alignItems="center"
                        sx={{
                          width: "fit-content",
                          height: "auto",
                          borderRadius: 1,
                          bgcolor: isDisabled
                            ? "#eeeeee"
                            : dayIndex % 2 === 0
                            ? "#fafafa"
                            : "#EBFAF9",
                          border: "1px solid #e0e0e0",
                          columnGap: "10px",
                          opacity: isDisabled ? 0.6 : 1, // faded look for occupied rows
                          pointerEvents: isDisabled ? "none" : "auto", // fully disables input interaction
                        }}
                      >
                        <Box
                          fontWeight={500}
                          sx={{
                            borderRight: "2px solid #ccc",
                            pr: 1,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                            {dayData.day}
                          </Box>
                          <Box
                            sx={{ fontWeight: "normal", fontSize: "0.7rem" }}
                          >
                            {dayData.date}
                          </Box>
                        </Box>

                        {dayData.shifts.map((shift, shiftIndex) => {
                          const invalidShift = invalidShifts.find(
                            (invalidShift) =>
                              invalidShift.day === dayData.day &&
                              invalidShift.shiftIndex === shiftIndex
                          );

                          return (
                            <Box key={shiftIndex} sx={{ display: "contents" }}>
                              {/* <TimePicker
                                ampm
                                format="hh:mm A"
                                value={shift.start}
                                disabled={isDisabled} // disable input
                                desktopModeMediaQuery="@media (pointer: none), (pointer: coarse)"
                                onChange={(val) => {
                                  handleTimeChange(
                                    dayIndex,
                                    shiftIndex,
                                    "start",
                                    val
                                  );
                                  setOpenTimePicker(false);
                                }}
                                
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    inputProps: {
                                      placeholder: "HH:MM",
                                      readOnly: true, // Disable keyboard input
                                      },
                                    sx: {
                                      minWidth: 90,
                                      "& .MuiInputBase-input": {
                                        textTransform: "uppercase",
                                        fontSize: "0.8rem",
                                        padding: "6px 4px",
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                        position: "absolute",
                                        right: "4px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      },
                                    },
                                    error:
                                      invalidShift && invalidShift.errorMessage,
                                  },
                                }}
                              /> */}

                              <MobileTimePicker
                                ampm
                                format="hh:mm A"
                                value={shift.start}
                                disabled={isDisabled}
                                desktopModeMediaQuery="@media (pointer: none), (pointer: coarse)" // mobile behavior on all devices
                                onChange={(val) => {
                                  handleTimeChange(
                                    dayIndex,
                                    shiftIndex,
                                    "start",
                                    val
                                  );
                                }}
                                // open={isPastWeek ? false : undefined} // Prevent the popup from opening if isPastWeek
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    InputProps: {
                                      endAdornment: (
                                        <AccessTimeIcon
                                          sx={{
                                            fontSize: 16,
                                            color: "action.active",
                                            position: "absolute",
                                            right: 4,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            pointerEvents: "none", // decorative only
                                          }}
                                        />
                                      ),
                                    },
                                    inputProps: {
                                      placeholder: "HH:MM",
                                      readOnly: true,
                                    },
                                    sx: {
                                      minWidth: 90,
                                      position: "relative",
                                      cursor: "pointer", //pointer cursor for entire field
                                      "& .MuiInputBase-input": {
                                        textTransform: "uppercase",
                                        fontSize: "0.8rem",
                                        padding: "6px 4px",
                                        cursor: "pointer", //ensures text area also shows pointer
                                        // color: isPastWeek()
                                        //   ? "black"
                                        //   : "inherit",
                                      },
                                      "& .MuiInputBase-root": {
                                        cursor: "pointer", //ensures outer wrapper also shows pointer
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                      },
                                    },
                                    error:
                                      invalidShift && invalidShift.errorMessage,
                                  },
                                }}
                              />

                              {/* <TimePicker
                                ampm
                                format="hh:mm A"
                                value={shift.end}
                                disabled={isDisabled} // disable input
                                onChange={(val) =>
                                  handleTimeChange(
                                    dayIndex,
                                    shiftIndex,
                                    "end",
                                    val
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    inputProps: {
                                      placeholder: "HH:MM",
                                      readOnly: true, // Disable keyboard input
                                    },
                                    sx: {
                                      minWidth: 90,
                                      "& .MuiInputBase-input": {
                                        textTransform: "uppercase",
                                        fontSize: "0.8rem",
                                        padding: "6px 4px",
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                        position: "absolute",
                                        right: "4px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      },
                                    },
                                    error:
                                      invalidShift && invalidShift.errorMessage,
                                  },
                                }}
                              /> */}
                              <MobileTimePicker
                                ampm
                                format="hh:mm A"
                                value={shift.end}
                                disabled={isDisabled}
                                desktopModeMediaQuery="@media (pointer: none), (pointer: coarse)" // mobile behavior on all devices
                                onChange={(val) => {
                                  handleTimeChange(
                                    dayIndex,
                                    shiftIndex,
                                    "end",
                                    val
                                  );
                                }}
                                // open={isPastWeek ? false : undefined} // Prevent the popup from opening if isPastWeek
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    InputProps: {
                                      endAdornment: (
                                        <AccessTimeIcon
                                          sx={{
                                            fontSize: 16,
                                            color: "action.active",
                                            position: "absolute",
                                            right: 4,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            pointerEvents: "none", // decorative only
                                          }}
                                        />
                                      ),
                                    },
                                    inputProps: {
                                      placeholder: "HH:MM",
                                      readOnly: true,
                                    },
                                    sx: {
                                      minWidth: 90,
                                      position: "relative",
                                      cursor: "pointer", //pointer cursor for entire field
                                      "& .MuiInputBase-input": {
                                        textTransform: "uppercase",
                                        fontSize: "0.8rem",
                                        padding: "6px 4px",
                                        cursor: "pointer", //ensures text area also shows pointer
                                        // color: isPastWeek()
                                        //   ? "black"
                                        //   : "inherit",
                                      },
                                      "& .MuiInputBase-root": {
                                        cursor: "pointer", //ensures outer wrapper also shows pointer
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                      },
                                    },
                                    error:
                                      invalidShift && invalidShift.errorMessage,
                                  },
                                }}
                              />

                              <TextField
                                type="number"
                                size="small"
                                placeholder="0"
                                value={shift.patientCount || ""}
                                disabled={isDisabled} // disable input
                                onChange={(e) =>
                                  handleTimeChange(
                                    dayIndex,
                                    shiftIndex,
                                    "patientCount",
                                    e.target.value
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  style: {
                                    textAlign: "center",
                                    fontSize: "0.8rem",
                                    padding: "6px 4px",
                                  },
                                }}
                                sx={{
                                  minWidth: 70,
                                  "& .MuiInputBase-input": {
                                    textAlign: "center",
                                  },
                                }}
                                error={
                                  invalidShift && invalidShift.errorMessage
                                }
                              />

                              {/* Reset Icon */}
                              <Tooltip title="Reset">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (!isPastWeek())
                                      handleResetShift(dayIndex, shiftIndex);
                                  }}
                                  disabled={isDisabled}
                                  sx={{
                                    color: "#666",
                                    "&:hover": { color: "#000" },
                                    "&.Mui-disabled": { color: "#bbb" },
                                  }}
                                >
                                  <RestartAltIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* Divider after each shift block */}
                              {/* {shiftIndex !== dayData.shifts.length - 1 && ( */}
                              <Box
                                sx={{
                                  borderRight: "2px solid #ddd",
                                  height: "100%",
                                }}
                              />
                              {/* )} */}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              </LocalizationProvider>
            </>
          )}
          {selectedTab === "doctor leaves" && (
            <DoctorLeaves doctorId={doctorId} />
          )}
          {/* </Box> */}
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end" gap={2} mr={3}>
            {/* Buttons */}

            <Button
              variant="contained"
              sx={{
                borderRadius: "10px",
                backgroundColor: "#121959",
                color: "#fff",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                "&:hover": {
                  backgroundColor: "#121959",
                },
              }}
              onClick={closeHandler}
              size="small"
            >
              Close
            </Button>

            {selectedTab === "custom schedule" &&
              !isPastWeek() && ( //cannot change schedule for past weeks
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: "10px",
                    backgroundColor: "#121959",
                    color: "#fff",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 1 },
                    "&:hover": {
                      backgroundColor: "#121959",
                    },
                  }}
                  onClick={onSave}
                  size="small"
                >
                  Save
                </Button>
              )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <Dialog
      open={open}
      onClose={closeHandler}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "90%", // responsive width (mobile-friendly)
          maxWidth: "1500px", // never exceed 1000px
          maxHeight: "90vh",
          zIndex: 9997,
        },
      }}
    >
      <DialogTitle
        sx={{
          // backgroundImage: `url(${Icons.modal_blue})`,
          backgroundColor: "#213360",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#FFFFFF",
            margin: "0px",
            fontSize: "20px",
          }}
        >
          Set Custom Schedule
        </div>
        <IconButton onClick={closeHandler}>
          <CloseIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          maxHeight: "calc(80vh - 100px)",
          overflowY: "auto",
          display: "flex",
          justifyContent: "center", // Horizontal centering
          alignItems: "center", // Vertical centering
          textAlign: "center", // Center the text inside
        }}
      >
        <div style={{ fontSize: "18px", color: "#0E3F66", padding: "20px" }}>
          No Schedules found for this Doctor! Please Contact Administrator
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDoctorScheduleModal;
